/* @flow */
import { combineReducers } from 'redux-immutable';
import { OrderedMap, fromJS } from 'immutable';
import type { Action } from 'trustar/utils/redux-types.js';

import { Attribute, EnclaveTag, IndicatorTag } from './types.js';
import type { RawTag, IndicatorTags } from './types.js';
import {
  FETCH_ATTRIBUTES,
  FETCH_ATTRIBUTES_SUCCESS,
  FETCH_ATTRIBUTES_FAILURE,

  FETCH_ENCLAVE_TAGS,
  FETCH_ENCLAVE_TAGS_SUCCESS,
  FETCH_ENCLAVE_TAGS_FAILURE,

  FETCH_INDICATOR_TAGS,
  FETCH_INDICATOR_TAGS_SUCCESS,
  FETCH_INDICATOR_TAGS_FAILURE,

  ADD_INDICATOR_TAG_SUCCESS,

  SET_MODAL_OPTIONS,
} from './action-types.js';
import {
  SET_ATTRIBUTE,
  SET_ATTRIBUTE_SUCCESS,
  SET_ATTRIBUTE_FAILURE,

  SET_ENCLAVE_TAG,
  SET_ENCLAVE_TAG_SUCCESS,
  SET_ENCLAVE_TAG_FAILURE,
} from 'modules/reports/action-types.js';

/* **************************************************************************
 * Tags
 * *************************************************************************/

export const tagsListToOrderedMap = (tags: RawTag[]): OrderedMap<any, any> => {
  return fromJS(tags)
    .toOrderedMap()
    .mapKeys((k, v) => v.get('guid'));
};

type State = OrderedMap<number, Attribute>;

const onSetTagSuccess = (state, { reportGuid, tag }) => {
  return state.set(tag.get('guid'), tag);
};

/**
 * NOTE: The attributes store and enclaveTags store are almost the same, they
 * could likely be refactored for less boilerplate. For now i'm opting for the
 * consistency and simplicity of separate reducers.
 */
export const attributes = (state: State = OrderedMap(), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_ATTRIBUTES_SUCCESS:
    return tagsListToOrderedMap(payload).map(Attribute);
  case SET_ATTRIBUTE_SUCCESS:
    return onSetTagSuccess(state, payload);
  default:
    return state;
  }
};

export const enclaveTags = (state: State = OrderedMap(), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
  case FETCH_ENCLAVE_TAGS_SUCCESS:
    return tagsListToOrderedMap(payload).map(EnclaveTag);
  case SET_ENCLAVE_TAG_SUCCESS:
    return onSetTagSuccess(state, payload);
  default:
    return state;
  }
};

export const indicatorTags = (state: IndicatorTags = OrderedMap(), action: Action<*>) => {
  const { type, payload } = action;

  switch (type) {
    case ADD_INDICATOR_TAG_SUCCESS:
      return state.updateIn([payload.enclaveId, payload.guid], () => IndicatorTag(payload));
  case FETCH_INDICATOR_TAGS_SUCCESS:
    return fromJS(payload).map(e => tagsListToOrderedMap(e).map(EnclaveTag));
  default:
    return state;
  }
};

export const loading = (state: boolean = false, action: Action<any>) => {
  switch (action.type) {
  case FETCH_ENCLAVE_TAGS:
  case FETCH_ATTRIBUTES:
  case SET_ENCLAVE_TAG:
  case SET_ATTRIBUTE:
  case FETCH_INDICATOR_TAGS:
    return true;
  case FETCH_ENCLAVE_TAGS_SUCCESS:
  case FETCH_ENCLAVE_TAGS_FAILURE:
  case FETCH_ATTRIBUTES_SUCCESS:
  case FETCH_ATTRIBUTES_FAILURE:
  case SET_ATTRIBUTE_SUCCESS:
  case SET_ATTRIBUTE_FAILURE:
  case SET_ENCLAVE_TAG_SUCCESS:
  case SET_ENCLAVE_TAG_FAILURE:
  case FETCH_INDICATOR_TAGS_SUCCESS:
  case FETCH_INDICATOR_TAGS_FAILURE:
    return false;
  default:
    return state;
  }
};

/*
 * NOTE: Modal serves report and indicator enclave tags. Because it only makes
 * sense for the modal to be open for one of these at one time, we can reuse this
 * generic "tags modal" state to manage both.
 */
export const modalOpen = (state: boolean = false, action: Action<any>) => {
  const { type, payload } = action;

  switch (type) {
  case SET_MODAL_OPTIONS:
    return payload;
  default:
    return state;
  }
};

export default combineReducers({
  attributes,
  enclaveTags,
  indicatorTags,
  loading,
  modalOpen,
});



// WEBPACK FOOTER //
// ./src/app/modules/tags/stores.js